/**
 * xr_locomotion — the PURE rules of VR locomotion and comfort (XR P2 comfort slice, 2026-09-28).
 *
 * AtlasXRModule (the session, the sticks), vr/VignetteMesh (the comfort vignette + the blink) and vr/TeleportArc
 * (the aim) run these; `verify:xr-locomotion` pins them. No three, no DOM, no SDK — numbers in, numbers out.
 *
 * What the research asked for (sources in atlas/docs/research/2026-09-28-xr/impl-xr-comfort.md):
 *  · TURNING — snap (discrete steps, one flick = one step, the comfort default) or smooth (an opt-in rate with a
 *    deadzone; continuous rotation is the strongest vection trigger, so the vignette covers it).
 *  · THE VIGNETTE — Fernandes & Feiner (IEEE 3DUI 2016): restrict the field of view with a soft-edged circular cut
 *    while VIRTUAL motion is under way (linear speed + angular velocity), restore it when the motion stops; ease in
 *    fast and out slower so it never pops (Unity XRI's tunnelling vignette exposes the same ease-in / ease-out).
 *    Only ARTIFICIAL motion drives it: the rig's own displacement and yaw, never the head's physical movement.
 *  · TELEPORT — an arc from the moving hand, a target validated on the client (ground facing up, dry, not in a wall)
 *    and AGAIN on the server (`xr_teleport` — Hard Rule #1), a short fade to black around the hop (the "blink").
 *    A hop is paid from THE MOVEMENT BUDGET (server/src/lib/move_budget.ts): the metres cost the same fixed steps
 *    walking them would, so hopping can never beat walking on average.
 *  · SEATED MODE + HEIGHT — the floor goes down by `floorOffsetM(comfort())` through an OFFSET REFERENCE SPACE
 *    (WebXR's own rig transform): the head pose stays real, the tracking origin moves. A recenter re-bases the same
 *    offset on the head's current floor position and heading.
 */
import { FIXED_DT, MOVE_SPEED_MPS } from '../../../shared/constants.js';
import type { Hand, XRComfort } from './xr_comfort.js';

// ─── Sticks ──────────────────────────────────────────────────────────────────────

/** Below this stick length nothing moves (the existing locomotion deadzone). */
export const STICK_DEADZONE = 0.15;
/** Snap turn fires past this and re-arms under SNAP_LATCH_OFF (one flick = one step). */
export const SNAP_LATCH_ON = 0.6;
export const SNAP_LATCH_OFF = 0.3;
/** Smooth turn ignores the stick below this (a resting thumb must never drift the view). */
export const SMOOTH_DEADZONE = 0.2;

/**
 * The thumbstick of one xr-standard gamepad: axes[2,3] on Quest, axes[0,1] on a 2-axis runtime. Axes arrays can
 * carry NULL entries (unused touchpad slots — `null.toFixed` once killed the whole poll): every read is guarded.
 */
export function stickOf(axes: ArrayLike<number | null | undefined> | null | undefined): { x: number; y: number } {
  if (!axes) return { x: 0, y: 0 };
  const ax = (i: number): number => { const v = axes[i]; return typeof v === 'number' && Number.isFinite(v) ? v : 0; };
  const high = axes.length > 2 && (ax(2) !== 0 || ax(3) !== 0 || Math.abs(ax(0)) + Math.abs(ax(1)) < 0.01);
  return high ? { x: ax(2), y: ax(3) } : { x: ax(0), y: ax(1) };
}

/** Which controller MOVES and which TURNS (the comfort page's "move hand"; the other hand turns). */
export function stickRoles(moveHand: Hand): { move: Hand; turn: Hand } {
  return moveHand === 'right' ? { move: 'right', turn: 'left' } : { move: 'left', turn: 'right' };
}

// ─── Turning ─────────────────────────────────────────────────────────────────────

export interface SnapLatch { armed: boolean }

/**
 * One frame of SNAP turning: the yaw change in radians (added to atlas.localYaw; a stick pushed RIGHT turns right,
 * which is a NEGATIVE yaw in the rig's convention). Latched so one flick is one step; mutates the latch.
 */
export function snapTurn(latch: SnapLatch, stickX: number, snapDeg: number): number {
  const x = Number.isFinite(stickX) ? stickX : 0;
  if (latch.armed && Math.abs(x) > SNAP_LATCH_ON) {
    latch.armed = false;
    return -Math.sign(x) * (snapDeg * Math.PI) / 180;
  }
  if (!latch.armed && Math.abs(x) < SNAP_LATCH_OFF) latch.armed = true;
  return 0;
}

/**
 * One frame of SMOOTH turning: the yaw change in radians. The stick past the deadzone is remapped to 0..1 and
 * squared-in (fine control near the centre), times `degPerS` at full deflection.
 */
export function smoothTurn(stickX: number, degPerS: number, dt: number): number {
  const x = Number.isFinite(stickX) ? stickX : 0;
  const a = Math.abs(x);
  if (a <= SMOOTH_DEADZONE || !(dt > 0)) return 0;
  const u = Math.min(1, (a - SMOOTH_DEADZONE) / (1 - SMOOTH_DEADZONE));
  const rate = (degPerS * Math.PI) / 180 * (u * (0.35 + 0.65 * u));
  return -Math.sign(x) * rate * Math.min(dt, 0.1); // a stalled frame never spins the view a quarter turn
}

/**
 * The walking intent from the move stick, HEAD-relative: `yaw` is where the head looks (the XR camera's world
 * heading, -Z forward), the result is the desktop intent convention (forward = (-sin, -cos)·yaw). Zero inside the
 * deadzone.
 */
export function headRelativeIntent(stickX: number, stickY: number, yaw: number): { x: number; z: number } {
  const s = Number.isFinite(stickX) ? stickX : 0;
  const f = Number.isFinite(stickY) ? -stickY : 0; // stick Y is DOWN-positive
  if (Math.hypot(s, f) < STICK_DEADZONE) return { x: 0, z: 0 };
  return { x: -Math.sin(yaw) * f + Math.cos(yaw) * s, z: -Math.cos(yaw) * f - Math.sin(yaw) * s };
}

// ─── The comfort vignette ────────────────────────────────────────────────────────

/** The aperture's inner edge (half-angle from each eye's axis) when fully OPEN — past Quest 3's ~55° half-FOV, so
 *  the first degrees of closing begin outside the picture (no edge pops in). */
export const VIGNETTE_OPEN_DEG = 60;
/** The narrowest opening on foot and while riding (vehicle comfort narrows further). At the default strength 0.6 a
 *  full-rate turn opens to ~37° and is dark by ~51° — the outer third of a Quest 3 eye. */
export const VIGNETTE_MIN_DEG = 22;
export const VIGNETTE_MIN_RIDE_DEG = 18;
/** The soft edge between clear and fully dark (degrees). */
export const VIGNETTE_FEATHER_DEG = 14;
/** Below this amount the mesh is hidden (zero cost at rest). */
export const VIGNETTE_VISIBLE_MIN = 0.02;
/** Ease time constants (s): close fast when motion starts, open more slowly — never a pop. */
export const VIGNETTE_ATTACK_S = 0.08;
export const VIGNETTE_RELEASE_S = 0.3;
/** Motion references: the speed and turn rate at which the motion term saturates. */
export const VIGNETTE_SPEED_DEAD_MPS = 0.4;
export const VIGNETTE_SPEED_FULL_MPS = 5;
export const VIGNETTE_SPEED_FULL_RIDE_MPS = 30;
export const VIGNETTE_YAW_DEAD_DPS = 8;
export const VIGNETTE_YAW_FULL_DPS = 80;
export const VIGNETTE_YAW_FULL_RIDE_DPS = 60;
/** Riding with vehicle comfort on multiplies the vignette's strength (fast artificial motion is the strongest trigger). */
export const VIGNETTE_RIDE_BOOST = 1.35;

export interface RigMotion { speedMps: number; yawRateDps: number; riding: boolean }

const clamp01 = (v: number): number => (v <= 0 || !Number.isFinite(v) ? 0 : v >= 1 ? 1 : v);

/** How far the vignette should close (0 = open … 1 = at its narrowest) for this motion and these choices. */
export function vignetteTarget(m: RigMotion, c: Pick<XRComfort, 'vignette' | 'vehicleComfort'>): number {
  if (!(c.vignette > 0)) return 0;
  const riding = m.riding;
  const lin = clamp01((m.speedMps - VIGNETTE_SPEED_DEAD_MPS) / ((riding ? VIGNETTE_SPEED_FULL_RIDE_MPS : VIGNETTE_SPEED_FULL_MPS) - VIGNETTE_SPEED_DEAD_MPS));
  const ang = clamp01((Math.abs(m.yawRateDps) - VIGNETTE_YAW_DEAD_DPS) / ((riding ? VIGNETTE_YAW_FULL_RIDE_DPS : VIGNETTE_YAW_FULL_DPS) - VIGNETTE_YAW_DEAD_DPS));
  const motion = 1 - (1 - lin) * (1 - ang); // both add; either alone can close it
  const boost = riding && c.vehicleComfort ? VIGNETTE_RIDE_BOOST : 1;
  return clamp01(c.vignette * motion * boost);
}

/** One frame of easing toward the target (frame-rate independent). */
export function easeVignette(current: number, target: number, dt: number): number {
  if (!(dt > 0)) return current;
  const tau = target > current ? VIGNETTE_ATTACK_S : VIGNETTE_RELEASE_S;
  const k = 1 - Math.exp(-Math.min(dt, 0.25) / tau);
  const v = current + (target - current) * k;
  return Math.abs(v - target) < 1e-4 ? target : v;
}

/** The aperture for an amount: the inner (clear) half-angle and the feather, degrees. */
export function vignetteAperture(amount: number, riding: boolean, vehicleComfort = true): { innerDeg: number; featherDeg: number } {
  const a = clamp01(amount);
  const min = riding && vehicleComfort ? VIGNETTE_MIN_RIDE_DEG : VIGNETTE_MIN_DEG;
  return { innerDeg: VIGNETTE_OPEN_DEG - a * (VIGNETTE_OPEN_DEG - min), featherDeg: VIGNETTE_FEATHER_DEG };
}

/**
 * The per-eye alpha the vignette material computes (the CPU mirror of its TSL): `cosToEye` is the cosine of the
 * angle between the fragment direction and the eye's own axis. 0 inside the aperture, 1 past the feather.
 */
export function vignetteAlpha(cosToEye: number, innerDeg: number, featherDeg: number): number {
  const cIn = Math.cos((innerDeg * Math.PI) / 180);
  const cOut = Math.cos((Math.min(179, innerDeg + featherDeg) * Math.PI) / 180);
  if (cosToEye >= cIn) return 0;
  if (cosToEye <= cOut) return 1;
  const t = (cIn - cosToEye) / (cIn - cOut);
  return t * t * (3 - 2 * t);
}

/** A frame's rig displacement larger than this — AND faster than RIG_JUMP_MPS — is a jump (a teleport, a
 *  correction, an origin rebase), never motion: it must not flash the vignette. */
export const RIG_JUMP_M = 8;
export const RIG_JUMP_MPS = 400;

export interface RigPose { x: number; y: number; z: number; yaw: number }

/** Wrap an angle to (−π, π]. */
export function wrapPi(a: number): number {
  if (!Number.isFinite(a)) return 0;
  let v = a % (2 * Math.PI);
  if (v > Math.PI) v -= 2 * Math.PI;
  else if (v <= -Math.PI) v += 2 * Math.PI;
  return v;
}

/**
 * The rig's ARTIFICIAL motion between two frames: speed and yaw rate. `excludedYaw` is yaw the frame applied as a
 * discrete step (a snap turn — comfortable by itself, it must not count as a turn rate). A jump reads as rest.
 */
export function rigMotion(prev: RigPose | null, cur: RigPose, dt: number, excludedYaw = 0): { speedMps: number; yawRateDps: number; jump: boolean } {
  if (!prev || !(dt > 0)) return { speedMps: 0, yawRateDps: 0, jump: false };
  const d = Math.hypot(cur.x - prev.x, cur.y - prev.y, cur.z - prev.z);
  const jump = !Number.isFinite(d) || (d > RIG_JUMP_M && d / dt > RIG_JUMP_MPS);
  const dy = wrapPi(cur.yaw - prev.yaw - excludedYaw);
  const yawJump = Math.abs(dy) > Math.PI / 3; // a 60°+ step in one frame is a snap / a recenter, never a rate
  return {
    speedMps: jump ? 0 : d / dt,
    yawRateDps: yawJump ? 0 : (Math.abs(dy) / dt) * (180 / Math.PI),
    jump,
  };
}

// ─── The blink (fade to black around a discontinuity) ────────────────────────────

/** Fade-out, the longest hold waiting on the server, fade-in (s). A blink is 100–200 ms in the comfort literature. */
export const BLINK_OUT_S = 0.09;
export const BLINK_HOLD_MAX_S = 1.2;
export const BLINK_IN_S = 0.16;

export type BlinkPhase = 'idle' | 'out' | 'hold' | 'in';

/** The blackout level (0 clear … 1 black) of a blink phase `t` seconds in. */
export function blinkLevel(phase: BlinkPhase, t: number): number {
  switch (phase) {
    case 'out': return clamp01(t / BLINK_OUT_S);
    case 'hold': return 1;
    case 'in': return 1 - clamp01(t / BLINK_IN_S);
    default: return 0;
  }
}

// ─── Teleport ────────────────────────────────────────────────────────────────────

/** The longest hop (m). MIRROR of server/src/reducers/xr_teleport.ts XR_HOP_MAX_M. */
export const XR_HOP_MAX_M = 5;
/** Shorter than this is no hop (a twitch on release). MIRROR of the server's XR_HOP_MIN_M. */
export const XR_HOP_MIN_M = 0.5;
/** The movement bank (MIRROR of server/src/lib/move_budget.ts): steps refilled per second and the most banked. */
export const BUDGET_STEP_REFILL_PER_S = 66;
export const BUDGET_STEP_BURST = 60;
/** The client never asks again sooner than this after a hop (the bank needs time to refill anyway). */
export const HOP_COOLDOWN_S = 0.6;

/** The fixed steps a hop of `d` metres costs: what walking it at the walk speed would take (MIRROR of the server). */
export function hopSteps(d: number): number {
  if (!(d > 0)) return 0;
  return Math.ceil(d / (MOVE_SPEED_MPS * FIXED_DT) - 1e-9);
}

/** Stick pushed forward this far starts aiming; the aim stays up until the stick comes back under AIM_RELEASE. */
export const AIM_START = 0.6;
export const AIM_RELEASE = 0.3;

/** The launch speed of the aim arc (m/s) and its gravity: ~5 m of range from the hand at 45°. */
export const ARC_SPEED_MPS = 7;
export const ARC_GRAVITY = 9.8;
export const ARC_STEP_S = 0.04;
export const ARC_MAX_S = 2.2;

/**
 * The aim arc: sample points of a projectile from `o` along unit `d` at ARC_SPEED under ARC_GRAVITY, cut when the
 * horizontal range reaches XR_HOP_MAX_M (the caller then DROPS straight down from the last point — aiming high is
 * "as far as a hop goes"). Returns flat [x,y,z,…].
 */
export function arcPoints(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, maxRange = XR_HOP_MAX_M, cx = ox, cz = oz): number[] {
  // The range is measured from (cx, cz) — the RIG, not the hand ~0.3 m ahead of it — so the drop point of a
  // "max range" aim is a hop the server will take.
  const len = Math.hypot(dx, dy, dz) || 1;
  const vx = (dx / len) * ARC_SPEED_MPS, vy = (dy / len) * ARC_SPEED_MPS, vz = (dz / len) * ARC_SPEED_MPS;
  const out: number[] = [ox, oy, oz];
  let px = ox, pz = oz;
  for (let t = ARC_STEP_S; t <= ARC_MAX_S + 1e-9; t += ARC_STEP_S) {
    let x = ox + vx * t, z = oz + vz * t;
    const y = oy + vy * t - 0.5 * ARC_GRAVITY * t * t;
    if (Math.hypot(x - cx, z - cz) > maxRange) {
      // Bisect the last step to the range circle (monotone enough along one 40 ms step).
      let lo = 0, hi = 1;
      for (let i = 0; i < 20; i++) { const m = (lo + hi) / 2; if (Math.hypot(px + (x - px) * m - cx, pz + (z - pz) * m - cz) > maxRange) hi = m; else lo = m; }
      const py = out[out.length - 2];
      x = px + (x - px) * lo; z = pz + (z - pz) * lo;
      out.push(x, py + (y - py) * lo, z);
      break;
    }
    out.push(x, y, z);
    px = x; pz = z;
  }
  return out;
}

export type HopRefusal = 'no-ground' | 'steep' | 'roof' | 'water' | 'wall' | 'too-far' | 'too-close' | 'riding' | 'indoors' | 'swimming' | 'cooldown';

export interface HopInput {
  fromX: number; fromZ: number;
  /** The landing surface: point, its normal's up component, how high it sits over the analytic ground. */
  hit: { x: number; y: number; z: number; ny: number; aboveGround: number } | null;
  /** The still water level over the landing XZ (null = dry ground). */
  waterLevel: number | null;
  /** The SERVER's sea rule says the landing is sea (the analytic ground ≤ the derived sea level + 0.5 — the Commons
   *  only). The client never proposes a hop the server will refuse, whatever the rendered water answers. */
  seaRule?: boolean;
  /** A wall at walking height on the way (the structure mask sampled along the segment, target included). */
  wallOnPath: boolean;
  riding: boolean; indoors: boolean; swimming: boolean;
  /** Seconds since the last hop was ASKED. */
  sinceLastHopS: number;
}

/** Up-facing enough to stand on (cos ~40°; IWSDK's "upward-normal validation" rejects steep slopes and ledges). */
export const HOP_MIN_NY = 0.76;
/** A landing this far above the analytic ground is a roof, not a floor (grading pads and plazas sit lower). */
export const HOP_MAX_ABOVE_GROUND_M = 1.4;
/** Water deeper than this over the landing refuses it. */
export const HOP_WATER_M = 0.15;

/** The client's verdict on a hop (the server judges again): the refusal, or the target. */
export function judgeHop(h: HopInput): { ok: true; x: number; z: number; d: number } | { ok: false; reason: HopRefusal } {
  if (h.riding) return { ok: false, reason: 'riding' };
  if (h.indoors) return { ok: false, reason: 'indoors' };
  if (h.swimming) return { ok: false, reason: 'swimming' };
  if (h.sinceLastHopS < HOP_COOLDOWN_S) return { ok: false, reason: 'cooldown' };
  const hit = h.hit;
  if (!hit || !Number.isFinite(hit.x) || !Number.isFinite(hit.z)) return { ok: false, reason: 'no-ground' };
  if (!(hit.ny >= HOP_MIN_NY)) return { ok: false, reason: 'steep' };
  if (hit.aboveGround > HOP_MAX_ABOVE_GROUND_M) return { ok: false, reason: 'roof' };
  if (h.seaRule || (h.waterLevel !== null && h.waterLevel > hit.y + HOP_WATER_M)) return { ok: false, reason: 'water' };
  if (h.wallOnPath) return { ok: false, reason: 'wall' };
  const d = Math.hypot(hit.x - h.fromX, hit.z - h.fromZ);
  if (d > XR_HOP_MAX_M + 1e-6) return { ok: false, reason: 'too-far' };
  if (d < XR_HOP_MIN_M) return { ok: false, reason: 'too-close' };
  return { ok: true, x: hit.x, z: hit.z, d };
}

/** Human words for a refusal (the status line; the marker turns red for any of them). */
export function hopRefusalText(r: HopRefusal | string): string {
  switch (r) {
    case 'no-ground': return 'No ground there';
    case 'steep': return 'Too steep to stand on';
    case 'roof': return 'Not onto a roof';
    case 'water': return 'Not into water — swim or walk';
    case 'wall': return 'A wall is in the way';
    case 'too-far': return 'Too far for one hop';
    case 'too-close': return 'Too close to hop';
    case 'riding': return 'Teleport is for walking — the vehicle drives';
    case 'indoors': return 'Indoors the stick walks';
    case 'swimming': return 'Swim to the shore';
    case 'cooldown': return 'Catch your breath';
    default: return String(r);
  }
}

/** Sample points along a segment every `stepM` (both ends included) — the wall test's path. */
export function segmentSamples(ax: number, az: number, bx: number, bz: number, stepM = 0.35): Array<[number, number]> {
  const len = Math.hypot(bx - ax, bz - az);
  const n = Math.max(1, Math.ceil(len / stepM));
  const out: Array<[number, number]> = [];
  for (let i = 1; i <= n; i++) { const t = i / n; out.push([ax + (bx - ax) * t, az + (bz - az) * t]); }
  return out;
}

// ─── Seated floor + recenter: the offset reference space ─────────────────────────

/** The heading (rotation about +Y) of an orientation quaternion, in the rig's convention (−Z forward, CCW +). */
export function yawOfQuat(qx: number, qy: number, qz: number, qw: number): number {
  // forward = q · (0,0,−1): fx = −2(xz + wy), fz = −(1 − 2(x² + y²))
  const fx = -2 * (qx * qz + qw * qy);
  const fz = -(1 - 2 * (qx * qx + qy * qy));
  if (Math.hypot(fx, fz) < 1e-6) return 0; // looking straight up / down: no heading
  return Math.atan2(-fx, -fz);
}

export interface Recenter { x: number; z: number; yaw: number }

/**
 * The originOffset of the offset reference space (an XRRigidTransform: position + orientation, M = T·R): the new
 * origin sits `floorOffsetM` BELOW the base floor (so every pose reads that much HIGHER), and — after a recenter —
 * at the head's captured floor position turned to its heading (so that head reads at x = z = 0, facing −Z).
 */
export function originOffset(floorOffsetM: number, rc: Recenter | null): { px: number; py: number; pz: number; qx: number; qy: number; qz: number; qw: number } {
  const yaw = rc ? rc.yaw : 0;
  return {
    px: rc ? rc.x : 0, py: -(Number.isFinite(floorOffsetM) ? floorOffsetM : 0), pz: rc ? rc.z : 0,
    qx: 0, qy: Math.sin(yaw / 2), qz: 0, qw: Math.cos(yaw / 2),
  };
}

/**
 * A base-space pose seen from the offset space: M⁻¹ · pose (the harness checks the offset with it, and the probe
 * compares the headset's view height against it). Returns the position and the heading.
 */
export function poseInOffsetSpace(o: ReturnType<typeof originOffset>, x: number, y: number, z: number, yaw: number): { x: number; y: number; z: number; yaw: number } {
  const oy = 2 * Math.atan2(o.qy, o.qw); // the offset's own yaw
  const dx = x - o.px, dy = y - o.py, dz = z - o.pz;
  const c = Math.cos(-oy), s = Math.sin(-oy);
  // rotate (dx, dz) about +Y by −oy (three's convention: x' = x·c + z·s, z' = −x·s + z·c)
  return { x: dx * c + dz * s, y: dy, z: -dx * s + dz * c, yaw: wrapPi(yaw - oy) };
}

// ─── The Recenter action (the UI calls it; the XR module performs it in the next XR frame) ───

let recenterPending = false;
/** Ask for a recenter: the next XR frame re-bases the floor + heading on where the head is now (with a blink). */
export function requestXrRecenter(): void { recenterPending = true; }
/** The XR module takes the request (true once per request). */
export function takeXrRecenter(): boolean { const p = recenterPending; recenterPending = false; return p; }
